import { Fragment, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, getApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtLog } from "@/lib/tz";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, Send, Loader2, FileText, RotateCcw, Database, Info, ExternalLink } from "lucide-react";
import { DOCS_ACCENT as ACCENT } from "@/lib/govDocs";

const EXAMPLES = [
  "What do I do if a journalist contacts me?",
  "How should sharps be disposed of?",
  "How often are policies reviewed?",
  "Who do I report a needle-stick injury to?",
];

const docHref = (s) => `/documents/${s.library}/${s.document_id}`;
// External NDIS Commission guidance is shown in teal with a "C" number, so it's never mistaken for our policy.
const NDIS_COLOUR = "#0f766e";
const chip = "inline-flex items-center justify-center min-w-[1.25rem] h-[1.1rem] px-1 rounded text-[10px] font-semibold text-white";

// Inline: **bold**, [S3] citations (links to our document) and [C1] citations (links to the Commission's page).
function inline(text, byN, byC) {
  const parts = String(text).split(/(\*\*[^*]+\*\*|\[[SC]\d{1,2}\])/g);
  return parts.map((p, i) => {
    if (/^\*\*[^*]+\*\*$/.test(p)) return <strong key={i}>{p.slice(2, -2)}</strong>;
    const c = p.match(/^\[C(\d{1,2})\]$/);
    if (c) {
      const s = byC[Number(c[1])];
      return s ? (
        <a key={i} href={s.url} target="_blank" rel="noopener noreferrer" title={`NDIS Commission: ${s.label}${s.heading ? ` › ${s.heading}` : ""}`}
          className={`${chip} mx-0.5 align-text-top`} style={{ backgroundColor: NDIS_COLOUR }}>C{c[1]}</a>
      ) : null;
    }
    const m = p.match(/^\[S(\d{1,2})\]$/);
    if (m) {
      const s = byN[Number(m[1])];
      return s ? (
        <Link key={i} to={docHref(s)} title={`${s.label} › ${s.heading || "Introduction"}`}
          className="inline-flex items-center justify-center min-w-[1.25rem] h-[1.1rem] px-1 mx-0.5 rounded text-[10px] font-semibold align-text-top text-white"
          style={{ backgroundColor: ACCENT }}>{m[1]}</Link>
      ) : null;
    }
    return <Fragment key={i}>{p}</Fragment>;
  });
}

// Plain text with "- " bullet lines -> paragraphs and lists (no HTML from the model is ever rendered).
function AnswerText({ text, sources, ndisSources }) {
  const byN = Object.fromEntries((sources || []).map((s) => [s.n, s]));
  const byC = Object.fromEntries((ndisSources || []).map((s) => [s.n, s]));
  const blocks = [];
  let list = null;
  String(text || "").split("\n").forEach((line) => {
    const t = line.trim();
    if (/^[-*•]\s+/.test(t)) {
      if (!list) { list = []; blocks.push({ list }); }
      list.push(t.replace(/^[-*•]\s+/, ""));
    } else {
      list = null;
      if (t) blocks.push({ p: t });
    }
  });
  return (
    <div className="space-y-2 text-sm text-slate-700 leading-relaxed">
      {blocks.map((b, i) => (b.list
        ? <ul key={i} className="list-disc pl-5 space-y-1">{b.list.map((li, j) => <li key={j}>{inline(li, byN, byC)}</li>)}</ul>
        : <p key={i}>{inline(b.p, byN, byC)}</p>))}
    </div>
  );
}

export default function PoliciesAI() {
  const { can, user } = useAuth();
  const isAdmin = can.has("docs.admin");
  const [messages, setMessages] = useState([]);   // { role, content, sources?, read_in_full? }
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);
  const [rebuilding, setRebuilding] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { if (can.management) api.govAiStatus().then(setStatus).catch(() => {}); }, [can.management]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [messages, busy]);

  const ask = async (question) => {
    const text = (question ?? q).trim();
    if (!text || busy) return;
    const history = messages.map((m) => ({ role: m.role, content: m.content }));
    setMessages((m) => [...m, { role: "user", content: text }]);
    setQ("");
    setBusy(true);
    try {
      const r = await api.govAsk(text, history);
      setMessages((m) => [...m, { role: "assistant", content: r.answer, sources: r.sources, read_in_full: r.read_in_full, ndis_sources: r.ndis_sources }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: getApiError(e, "Something went wrong. Please try again."), error: true }]);
    } finally { setBusy(false); }
  };

  const rebuild = async () => {
    setRebuilding(true);
    try {
      const r = await api.govAiReindex();
      setStatus(r);
      toast.success(`Search index rebuilt: ${r.documents} documents, ${r.sections} sections.`);
    } catch (e) { toast.error(getApiError(e, "Couldn't rebuild the index.")); }
    finally { setRebuilding(false); }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto flex flex-col min-h-[calc(100vh-3.5rem)]" data-testid="policies-ai">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2.5">
            <Sparkles className="h-5 w-5" style={{ color: ACCENT }} />
            <h1 className="text-xl font-semibold font-brand text-slate-800">Policies AI</h1>
          </div>
          <p className="text-xs text-slate-500 mt-0.5">
            Ask about our current policies, procedures and company documents. Answers link to the documents they come from.
          </p>
        </div>
        {messages.length > 0 && (
          <Button size="sm" variant="outline" className="h-8 text-xs shrink-0" onClick={() => setMessages([])} disabled={busy} data-testid="ai-new-chat">
            <RotateCcw className="h-3.5 w-3.5 mr-1" /> New conversation
          </Button>
        )}
      </div>

      <div className="flex items-start gap-2 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 mt-4 text-[11px] text-slate-500">
        <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
        The AI uses our documents that are currently Active, plus NDIS Commission guidance (shown in teal with a C number), and it can make mistakes. Check the linked document before acting on an answer, and ask your manager if you're unsure.
      </div>

      <div className="flex-1 mt-4 space-y-4" data-testid="ai-messages">
        {!messages.length && (
          <div className="text-center py-10">
            <div className="text-sm text-slate-500 mb-3">Try asking:</div>
            <div className="flex flex-wrap justify-center gap-2">
              {EXAMPLES.map((e) => (
                <button key={e} onClick={() => ask(e)} className="text-xs rounded-full border border-slate-200 bg-white px-3 py-1.5 text-slate-600 hover:border-[#6b4f8a] hover:text-[#6b4f8a]">{e}</button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (m.role === "user" ? (
          <div key={i} className="flex justify-end">
            <div className="max-w-[80%] rounded-2xl rounded-br-sm px-4 py-2 text-sm text-white whitespace-pre-line" style={{ backgroundColor: ACCENT }}>{m.content}</div>
          </div>
        ) : (
          <div key={i} className="flex gap-2.5" data-testid="ai-answer">
            <div className="h-7 w-7 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: `${ACCENT}1a` }}>
              <Sparkles className="h-3.5 w-3.5" style={{ color: ACCENT }} />
            </div>
            <div className={`flex-1 min-w-0 rounded-2xl rounded-tl-sm border px-4 py-3 ${m.error ? "bg-rose-50 border-rose-200" : "bg-white border-slate-200"}`}>
              <AnswerText text={m.content} sources={m.sources} ndisSources={m.ndis_sources} />
              {(m.sources?.length > 0 || m.read_in_full?.length > 0 || m.ndis_sources?.length > 0) && (
                <div className="mt-3 pt-2 border-t border-slate-100 space-y-1" data-testid="ai-sources">
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Sources</div>
                  {m.sources.map((s) => (
                    <Link key={s.n} to={docHref(s)} className="flex items-start gap-1.5 text-xs text-slate-600 hover:text-[#6b4f8a]">
                      <span className="inline-flex items-center justify-center min-w-[1.25rem] h-[1.1rem] px-1 rounded text-[10px] font-semibold text-white shrink-0" style={{ backgroundColor: ACCENT }}>{s.n}</span>
                      <span>{s.label}{s.version ? ` (v${s.version})` : ""}{s.heading ? <span className="text-slate-400"> › {s.heading}</span> : null}</span>
                    </Link>
                  ))}
                  {m.ndis_sources?.map((s) => (
                    <a key={`c${s.n}`} href={s.url} target="_blank" rel="noopener noreferrer" className="flex items-start gap-1.5 text-xs text-slate-600 hover:text-[#0f766e]" data-testid="ai-ndis-source">
                      <span className={`${chip} shrink-0`} style={{ backgroundColor: NDIS_COLOUR }}>C{s.n}</span>
                      <span>
                        <span className="font-medium text-[#0f766e]">NDIS Commission guidance:</span> {s.label}
                        {s.heading ? <span className="text-slate-400"> › {s.heading}</span> : null}
                        {s.updated ? <span className="text-slate-400"> · updated {s.updated.split("-").reverse().join("/")}</span> : null}
                        <ExternalLink className="h-3 w-3 inline ml-1 text-slate-400" />
                      </span>
                    </a>
                  ))}
                  {m.read_in_full?.map((d) => (
                    <Link key={d.document_id} to={docHref(d)} className="flex items-center gap-1.5 text-xs text-slate-600 hover:text-[#6b4f8a]">
                      <FileText className="h-3.5 w-3.5 text-slate-400" /> {d.label} <span className="text-slate-400">(read in full)</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        )))}
        {busy && (
          <div className="flex items-center gap-2 text-xs text-slate-500 pl-10" data-testid="ai-thinking">
            <Loader2 className="h-3.5 w-3.5 animate-spin" style={{ color: ACCENT }} /> Searching the documents…
          </div>
        )}
        <div ref={endRef} />
      </div>

      <div className="sticky bottom-0 bg-[#fafafa] pt-3 pb-2 mt-4">
        <div className="flex items-end gap-2 rounded-xl border border-slate-200 bg-white p-2 focus-within:border-[#6b4f8a]">
          <Textarea value={q} onChange={(e) => setQ(e.target.value)} rows={2} maxLength={1000} placeholder="Ask a question about our policies…"
            className="border-0 shadow-none focus-visible:ring-0 resize-none text-sm min-h-[44px]" data-testid="ai-input"
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(); } }} />
          <Button size="sm" className="h-9 shrink-0" style={{ backgroundColor: ACCENT }} onClick={() => ask()} disabled={busy || !q.trim()} data-testid="ai-send">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
        <div className="text-[10px] text-slate-400 mt-1 px-1">Enter to send, Shift+Enter for a new line. Conversations aren't saved.</div>
        {can.management && status && (
          <div className="flex items-center justify-between gap-2 mt-2 px-1 text-[10px] text-slate-400" data-testid="ai-index-status">
            <span className="inline-flex items-center gap-1"><Database className="h-3 w-3" />
              Search index: {status.documents} of {status.active_documents} Active documents, {status.sections} sections
              {status.without_embeddings ? ` (${status.without_embeddings} keyword-only)` : ""}
              {status.updated_at ? ` · updated ${fmtLog(status.updated_at)}` : ""}</span>
            {isAdmin && (
              <button className="hover:text-[#6b4f8a] disabled:opacity-50" onClick={rebuild} disabled={rebuilding} data-testid="ai-reindex">
                {rebuilding ? "Rebuilding…" : "Rebuild index"}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
